import type { DrawingImage } from "@mnk/shared/models/drawingImage";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadIcon from "@mui/icons-material/Download";
import MinusIcon from "@mui/icons-material/Remove";
import { useEffect, useState } from "react";

interface Props {
  zoom: number;
  minZoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomChange: (value: number) => void;
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  drawingImages: DrawingImage[];
  showFlag: boolean;
  setShowFlag: (showFlag: boolean) => void;
  showDownloader: boolean;
  setShowDownloader: (showDownloader: boolean) => void;
}

export const Toolbar = ({
  zoom,
  minZoom,
  onZoomIn,
  onZoomOut,
  onZoomChange,
  currentIndex,
  setCurrentIndex,
  drawingImages,
  showFlag,
  setShowFlag,
  showDownloader,
  setShowDownloader,
}: Props) => {
  const drawingImage = drawingImages[currentIndex];
  const [inputValue, setInputValue] = useState(String(Math.round(zoom * 100)));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setInputValue(String(Math.round(zoom * 100)));
    }
  }, [zoom, isFocused]);

  const handleCommit = () => {
    const parsed = Number.parseInt(inputValue, 10);
    const minPercent = Math.ceil(minZoom * 100);
    if (!Number.isNaN(parsed)) {
      const clamped = Math.min(Math.max(parsed, minPercent), 200);
      onZoomChange(clamped / 100);
      setInputValue(String(clamped));
    } else {
      setInputValue(String(Math.round(zoom * 100)));
    }
    setIsFocused(false);
  };

  return (
    <div className="flex flex-row justify-between items-center bg-gray-100 rounded-t-md border border-gray-300">
      <div>
        {drawingImage && (
          <p className="text-gray-700 px-4">
            {drawingImage.startKiloPost === 10000000 ? (
              <span>{drawingImage.sectionName}</span>
            ) : (
              <span>
                KP: {drawingImage.startKiloPost.toFixed(2)} 〜 {drawingImage.endKiloPost.toFixed(2)}
              </span>
            )}
          </p>
        )}
      </div>
      <div className="flex flex-row gap-4 items-center p-2">
        {drawingImages?.length && drawingImages.length > 1 && (
          <>
            <button
              type="button"
              className="px-3 py-2 border rounded-md transition-colors bg-white text-gray-700 border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              onClick={() => setShowFlag(!showFlag)}
            >
              {showFlag ? "旗揚げを非表示にする" : "旗揚げを表示する"}
            </button>
            <button
              type="button"
              className="px-3 py-2 border rounded-md transition-colors bg-white text-gray-700 border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              onClick={() => setCurrentIndex(currentIndex - 1)}
              disabled={currentIndex === 0}
            >
              <ArrowBackIcon className="w-4 h-4" />
            </button>
            <span className="text-gray-700 flex flex-row items-center gap-3">
              <select
                className="h-10 w-16 text-center border border-gray-300 rounded-md bg-white text-lg focus:outline-none focus:ring-2 focus:ring-blue-200 appearance-none"
                style={{ minWidth: 56, paddingRight: 24 }}
                value={currentIndex}
                onChange={(e) => setCurrentIndex(Number(e.target.value))}
              >
                {drawingImages.map((_, index) => (
                  <option key={`page-${index + 1}`} value={index}>
                    {index + 1}
                  </option>
                ))}
              </select>
              <span className="ml-2">/ {drawingImages?.length}</span>
            </span>
            <button
              type="button"
              className="px-3 py-2 border rounded-md transition-colors bg-white text-gray-700 border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              onClick={() => setCurrentIndex(currentIndex + 1)}
              disabled={currentIndex === drawingImages.length - 1}
            >
              <ArrowForwardIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="px-3 py-2 border rounded-md transition-colors bg-white text-gray-700 border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              onClick={onZoomOut}
              disabled={zoom <= minZoom}
            >
              <MinusIcon className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1">
              <input
                type="text"
                inputMode="numeric"
                value={inputValue}
                className="w-14 text-center border border-gray-300 rounded-md bg-white text-gray-700 py-1 focus:outline-none focus:ring-2 focus:ring-blue-200"
                onChange={(e) => setInputValue(e.target.value)}
                onFocus={(e) => {
                  setIsFocused(true);
                  e.target.select();
                }}
                onBlur={handleCommit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCommit();
                  if (e.key === "Escape") {
                    setInputValue(String(Math.round(zoom * 100)));
                    setIsFocused(false);
                  }
                }}
              />
              <span className="text-gray-700">%</span>
            </div>
            <button
              type="button"
              className="px-3 py-2 border rounded-md transition-colors bg-white text-gray-700 border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              onClick={onZoomIn}
              disabled={zoom >= 2.0}
            >
              <AddIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="px-3 py-2 border rounded-md transition-colors bg-white text-gray-700 border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              onClick={() => setShowDownloader(!showDownloader)}
            >
              <DownloadIcon className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};
